import { useEffect, useMemo, useState } from "react";
import { formatUzPhone, isBlankPhoneInput } from "../phone";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import { Plus, X, Receipt, Ban, PackageSearch, Pencil, Undo2 } from "lucide-react";
import {
  api,
  fmtMoney,
  type ClientDetails,
  type PaymentMethod,
  type PaymentMethodsOut,
  type Product,
  type Sale,
} from "../api";
import { fmtDateLong } from "../lib/format";
import { useBranch, useBranchLookup } from "../lib/use-branch";
import { BranchLabel } from "../components/branch-label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog } from "../components/ui/dialog";
import { EmptyState } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { MoneyInput } from "../components/money-input";
import { Skeleton } from "../components/ui/skeleton";
import { ProductCombobox } from "../components/product-combobox";
import { QtyStepper } from "../components/qty-stepper";
import { PaymentEditDialog } from "../components/payment-edit-dialog";
import { PaymentMethodBreakdown } from "../components/payment-method-breakdown";
import { PaymentMethodSelect } from "../components/payment-method-select";
import {
  PaymentMethodCorrectionDialog,
  PaymentMethodValue,
} from "../components/payment-method-correction";
import { ConfirmDialog } from "../components/confirm-dialog";
import { ReturnDialog } from "../components/return-dialog";
import { DateColumn, LedgerDayTable, buildDateGroups } from "../components/date-ledger";
import { useFocusDoc } from "../lib/use-focus-doc";
import {
  buildReturnedIndex,
  docHasReturnable,
  fetchAllReturns,
  type ReturnedIndex,
} from "../lib/returns-index";
import { cn } from "../lib/utils";

type Line = { product: Product; miqdor: string; sotuv_narx: string };

const PAGE = 200;

function paymentLabel(t: TFunction, status: Sale["tolov_holati"]): string {
  return status === "tolandi"
    ? t("sotuvlar.tolov.tolandi")
    : status === "qisman"
      ? t("sotuvlar.tolov.qisman")
      : t("sotuvlar.tolov.tolanmagan");
}

function PaymentBadge({ status, t }: { status: Sale["tolov_holati"]; t: TFunction }) {
  const variant = status === "tolandi" ? "success" : status === "qisman" ? "secondary" : "destructive";
  return <Badge variant={variant}>{paymentLabel(t, status)}</Badge>;
}

export function SotuvlarPage({
  initialProduct,
  openSignal,
  focusDoc,
}: {
  initialProduct?: Product | null;
  openSignal?: number;
  /** Tovar jurnalidan kelingan hujjat — tanlanadi va ajratib ko'rsatiladi. */
  focusDoc?: { id: number; seq: number } | null;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  // Ro'yxat ham topbar'dagi filial selektoriga bo'ysunadi (ustun bilan
  // yonma-yon turgan selektor ishlamasa chalkashlik bo'lardi). Yagona
  // filialda `showSelector` false — ustun ham, filtr ham yo'q.
  const { selectedBranchId, showSelector } = useBranch();
  const branchOf = useBranchLookup();
  const [items, setItems] = useState<Sale[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [creating, setCreating] = useState(false);
  const [payEdit, setPayEdit] = useState<Sale | null>(null);
  // Usulni to'g'rilash — to'lov holatidan QAT'I NAZAR ochiladi (to'liq
  // to'langan hujjatda `payEdit` umuman ko'rinmaydi).
  const [methodEdit, setMethodEdit] = useState<Sale | null>(null);
  // Usul bo'linishi — ro'yxat bilan bir xil filial doirasida.
  const [methods, setMethods] = useState<PaymentMethodsOut | null>(null);
  // Bekor qilish qaytarib bo'lmaydi (zaxira va qarz daftari orqaga yoziladi) —
  // shuning uchun tasdiqlash oynasidan o'tadi.
  const [cancelTarget, setCancelTarget] = useState<Sale | null>(null);
  const [returnDoc, setReturnDoc] = useState<Sale | null>(null);
  const [retIndex, setRetIndex] = useState<ReturnedIndex>(new Map());
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const pg = await api.sales({
          page: 1,
          page_size: PAGE,
          branch_id: selectedBranchId ?? undefined,
        });
        // Filial almashsa ro'yxat NOLDAN quriladi — "ko'proq yuklash"
        // to'plagichi ikki filialning hujjatlarini aralashtirib yubormasin.
        setItems(pg.items);
        setTotal(pg.total);
        setPage(1);
        setErr(null);
      } catch (e) {
        setErr((e as { message?: string }).message || t("common:common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh, selectedBranchId, t]);

  // Usul bo'linishi — ro'yxat bilan bir xil filial doirasida, alohida so'rov
  // (ro'yxat generic `Page<Sale>` qaytaradi, unga qo'shimcha maydon sig'maydi).
  // Sahifalashga bog'liq EMAS: u butun davrni yig'adi, ekranga tushgan
  // qatorlarnigina emas.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const m = await api.salesPaymentMethods({
          branch_id: selectedBranchId ?? undefined,
        });
        if (active) setMethods(m);
      } catch {
        if (active) setMethods(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [refresh, selectedBranchId]);

  // Qaytarilgan miqdorlar indeksi — "qaytariladigan qoldiq bormi" tugma holati
  // uchun. Yuklab bo'lmasa indeks bo'sh qoladi (tugma faol — backend baribir
  // tekshiradi).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const docs = await fetchAllReturns("sale_return");
        if (alive) setRetIndex(buildReturnedIndex(docs));
      } catch {
        /* empty */
      }
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (initialProduct) setCreating(true);
  }, [initialProduct]);

  // Tablar qatoridagi "Yangi sotuv" tugmasi (OmborPage) signal orqali ochadi.
  useEffect(() => {
    if (openSignal) setCreating(true);
  }, [openSignal]);

  // Sana guruhlari — kunlik jamiga faqat faol sotuvlar kiradi (bekor qilingan
  // hujjat ro'yxatda ko'rinadi, lekin summaga qo'shilmaydi).
  // Jurnaldan kelingan hujjat — ro'yxatga QO'SHIB beriladi (uning holatiga
  // yozilmaydi), shuning uchun qayta yuklash uni yuvib yubormaydi.
  const { items: shownItems, highlightId } = useFocusDoc<Sale>({
    focusDoc,
    items,
    fetchOne: api.getSale,
    onSelectDate: setSelectedDate,
  });

  const groups = useMemo(
    () =>
      buildDateGroups(
        shownItems,
        (s) => s.created_at,
        (s) => (s.status === "active" ? Number(s.net_total ?? s.jami_summa) : 0),
      ),
    [shownItems],
  );

  useEffect(() => {
    if (groups.length && (selectedDate === null || !groups.some((g) => g.key === selectedDate))) {
      setSelectedDate(groups[0].key);
    }
  }, [groups, selectedDate]);

  const selectedGroup = groups.find((g) => g.key === selectedDate) ?? null;
  const hasMore = items.length < total;

  async function loadMore() {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const pg = await api.sales({
        page: next,
        page_size: PAGE,
        branch_id: selectedBranchId ?? undefined,
      });
      setItems((prev) => [...prev, ...pg.items]);
      setTotal(pg.total);
      setPage(next);
    } catch {
      /* empty */
    } finally {
      setLoadingMore(false);
    }
  }

  async function cancel(id: number) {
    try {
      await api.cancelSale(id);
      toast.success(t("sotuvlar.cancelled"));
      setRefresh((x) => x + 1);
    } catch (e) {
      toast.error((e as { message?: string }).message || t("common:common.error"));
      // Xatoni yuqoriga uzatamiz — tasdiqlash oynasi ochiq qolib, ega qayta
      // urinishi mumkin (yopilib ketsa hujjat qaysi ekani yo'qolardi).
      throw e;
    }
  }

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <PaymentMethodBreakdown
        data={methods}
        title={t("common:paymentMethod.salesTitle")}
        tooltip={t("common:paymentMethod.salesTooltip")}
      />

      {loading ? (
        <Card>
          <CardContent className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </CardContent>
        </Card>
      ) : shownItems.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={<Receipt />}
              title={t("sotuvlar.empty.title")}
              description={t("sotuvlar.empty.desc")}
              action={<Button onClick={() => setCreating(true)}><Plus />{t("sotuvlar.newSale")}</Button>}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
          <DateColumn
            totalLabel={t("shared.netReturn.net")}
            groups={groups.map((g) => ({
              key: g.key,
              total: g.total,
              count: g.items.length,
              repISO: g.repISO,
              debt: g.items.reduce(
                (s, x) =>
                  s +
                  (x.status === "active"
                    ? Number(x.remaining_amount ?? Math.max(0, Number(x.jami_summa) - Number(x.tolangan_summa)))
                    : 0),
                0,
              ),
            }))}
            selectedKey={selectedDate}
            onSelect={setSelectedDate}
            labelOf={(iso) => fmtDateLong(iso)}
            countLabel={(n) => t("sotuvlar.count", { count: n })}
            hasMore={hasMore}
            onLoadMore={loadMore}
            loadingMore={loadingMore}
            loadMoreLabel={t("shared.ledger.loadMore")}
          />

          <Card>
            <CardContent className="p-0">
              {selectedGroup && (
                <>
                  <div className="border-b p-3">
                    <div className="text-sm font-semibold">{fmtDateLong(selectedGroup.repISO)}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("sotuvlar.count", { count: selectedGroup.items.length })} · {t("shared.netReturn.net")}: {fmtMoney(selectedGroup.total)}
                    </div>
                  </div>
                  <LedgerDayTable
                    docs={selectedGroup.items.map((s) => ({
                      id: s.id,
                      party: s.client_name || t("sotuvlar.anonymous"),
                      branch: showSelector ? (
                        <BranchLabel
                          name={branchOf(s.branch_id)?.name}
                          isActive={branchOf(s.branch_id)?.is_active}
                        />
                      ) : undefined,
                      timeISO: s.created_at,
                      badge:
                        s.status === "cancelled" ? (
                          <Badge variant="destructive">{t("sotuvlar.status.bekor")}</Badge>
                        ) : (
                          <PaymentBadge status={s.tolov_holati} t={t} />
                        ),
                      method: (
                        <PaymentMethodValue
                          usul={s.tolov_usuli}
                          payments={s.payments}
                          // Bekor qilingan sotuvda to'g'rilash yo'q — server
                          // ham 409 `sale_cancelled` beradi, va bu qatorda
                          // boshqa amallar ham ko'rsatilmaydi.
                          onCorrect={
                            s.status === "cancelled"
                              ? undefined
                              : () => setMethodEdit(s)
                          }
                        />
                      ),
                      total: Number(s.jami_summa),
                      remaining: s.remaining_amount,
                      economics: Number(s.returned_total) > 0 ? (
                        <div className="text-xs text-muted-foreground">
                          {t("shared.netReturn.original")}: {fmtMoney(s.jami_summa)} · {t("shared.netReturn.returned")}: {fmtMoney(s.returned_total!)}
                          <br />{t("shared.netReturn.net")}: {fmtMoney(s.net_total!)}
                        </div>
                      ) : undefined,
                      paid: Number(s.tolangan_summa),
                      profit: s.foyda,
                      cancelled: s.status === "cancelled",
                      highlighted: s.id === highlightId,
                      lines: s.lines,
                      actions:
                        s.status === "cancelled" ? null : (
                          <div className="flex items-center justify-center gap-0.5">
                            {Number(s.remaining_amount ?? (Number(s.jami_summa) - Number(s.tolangan_summa))) > 0 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={() => setPayEdit(s)}
                                title={t("shared.paymentEdit.title")}
                                aria-label={t("shared.paymentEdit.title")}
                              >
                                <Pencil className="size-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-8 text-muted-foreground hover:text-destructive"
                              onClick={() => setCancelTarget(s)}
                              title={t("sotuvlar.cancel")}
                              aria-label={t("sotuvlar.cancel")}
                            >
                              <Ban className="size-4" />
                            </Button>
                            {(() => {
                              const returnable = docHasReturnable(s.id, s.lines, retIndex);
                              return (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className={cn("size-8", !returnable && "opacity-60")}
                                  onClick={() =>
                                    returnable
                                      ? setReturnDoc(s)
                                      : toast.info(t("shared.return.nothingToast"))
                                  }
                                  title={
                                    returnable
                                      ? t("shared.return.saleTitle")
                                      : t("shared.return.nothingReturnable")
                                  }
                                  aria-label={t("shared.return.action")}
                                >
                                  <Undo2 className="size-4" />
                                </Button>
                              );
                            })()}
                          </div>
                        ),
                    }))}
                    headers={{
                      party: t("sotuvlar.table.mijoz"),
                      tovar: t("sotuvlar.form.tovar"),
                      narx: t("sotuvlar.form.narx"),
                      miqdor: t("sotuvlar.form.miqdor"),
                      jami: t("sotuvlar.form.jami"),
                      tolangan: t("sotuvlar.table.tolangan"),
                      foyda: t("sotuvlar.table.foyda"),
                      // Berilmasa ustun umuman chizilmaydi (`foyda` bilan bir
                      // xil naqsh) — yagona filialda bo'sh ustun qolmaydi.
                      branch: showSelector ? t("shared.ledger.branch") : undefined,
                      vaqt: t("sotuvlar.table.vaqt"),
                      amal: t("sotuvlar.table.amallar"),
                    }}
                    qarzLabel={t("sotuvlar.form.qarz")}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={creating}
        onOpenChange={(o) => !o && setCreating(false)}
        contentClassName="max-w-2xl p-0"
      >
        {creating && (
          <SotuvForm
            initialProduct={initialProduct ?? null}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              setRefresh((x) => x + 1);
            }}
          />
        )}
      </Dialog>

      {payEdit && (
        <PaymentEditDialog
          open={!!payEdit}
          onOpenChange={(o) => !o && setPayEdit(null)}
          jami={Number(payEdit.jami_summa)}
          initialPaid={Number(payEdit.tolangan_summa)}
          partyLabel={payEdit.client_name || t("sotuvlar.anonymous")}
          onSave={async (body) => {
            await api.updateSalePayment(payEdit.id, body);
            setRefresh((x) => x + 1);
          }}
        />
      )}

      {methodEdit && (
        <PaymentMethodCorrectionDialog
          open={!!methodEdit}
          onOpenChange={(o) => !o && setMethodEdit(null)}
          current={methodEdit.tolov_usuli}
          partyLabel={methodEdit.client_name || t("sotuvlar.anonymous")}
          onSave={async (usul) => {
            await api.correctSaleMethod(methodEdit.id, usul);
            // Ro'yxat ham, usul bo'linishi ham qayta o'qiladi — sahifa
            // yangilanmaydi.
            setRefresh((x) => x + 1);
          }}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          open={!!cancelTarget}
          onOpenChange={(o) => !o && setCancelTarget(null)}
          destructive
          title={t("sotuvlar.cancelConfirm.title")}
          description={t("sotuvlar.cancelConfirm.description", {
            mijoz: cancelTarget.client_name || t("sotuvlar.anonymous"),
            summa: fmtMoney(cancelTarget.jami_summa),
          })}
          confirmLabel={t("sotuvlar.cancelConfirm.confirm")}
          onConfirm={() => cancel(cancelTarget.id)}
        />
      )}

      {returnDoc && (
        <ReturnDialog
          open={!!returnDoc}
          onOpenChange={(o) => !o && setReturnDoc(null)}
          kind="sale_return"
          docId={returnDoc.id}
          partyLabel={returnDoc.client_name || t("sotuvlar.anonymous")}
          lines={returnDoc.lines}
          onDone={() => setRefresh((x) => x + 1)}
        />
      )}
    </div>
  );
}

export function SotuvForm({
  initialProduct,
  onCancel,
  onSaved,
}: {
  initialProduct: Product | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const { selectedBranchId } = useBranch();
  const [products, setProducts] = useState<Product[]>([]);
  const [lines, setLines] = useState<Line[]>(() =>
    initialProduct ? [{ product: initialProduct, miqdor: "1", sotuv_narx: initialProduct.sotuv_narx }] : [],
  );
  // Mijoz — yozilganda avtomatik mos kelganlar; tanlasa to'ladi, "Yangi mijoz"
  // tugmasi yangi mijoz qo'shadi (ism + telefon + avto raqam); bo'sh = anonim.
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState<ClientDetails[]>([]);
  const [client, setClient] = useState<ClientDetails | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("+998");
  const [newPlate, setNewPlate] = useState("");
  // To'langan summa — qarz manbasi. Foydalanuvchi tahrirlamaguncha jamiga teng.
  const [paid, setPaid] = useState("");
  const [paidTouched, setPaidTouched] = useState(false);
  // Ixtiyoriy: tanlanmasa hujjat "noma'lum" guruhida qoladi.
  // Sukut bo'yicha naqd: bu yerda odam aynan hozir to'lovni yozyapti. Bo'sh
  // ("yozilmagan") tanlov ro'yxatda qoladi — bir bosish bilan tanlanadi.
  const [usul, setUsul] = useState<PaymentMethod | "">("naqd");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Tovarlar — sotuvchi filiali kesimida (max tarif: ostatka shu filialga oid).
  useEffect(() => {
    (async () => {
      try {
        setProducts(await api.products({ faol: true, branch_id: selectedBranchId ?? undefined }));
      } catch { /* empty */ }
    })();
  }, [selectedBranchId]);

  // Mijoz qidiruvi — debounce bilan avtomatik (alohida tugmasiz).
  useEffect(() => {
    if (client) return;
    const q = clientQuery.trim();
    if (!q) {
      setClientResults([]);
      return;
    }
    const id = setTimeout(async () => {
      try {
        setClientResults(await api.searchClients(q));
      } catch {
        setClientResults([]);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [clientQuery, client]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.miqdor) || 0) * (Number(l.sotuv_narx) || 0), 0),
    [lines],
  );

  // Tahrirlanmagan bo'lsa to'langan summa avtomatik jamiga kuzatib boradi.
  useEffect(() => {
    if (!paidTouched) setPaid(total ? String(total) : "");
  }, [total, paidTouched]);

  const paidNum = paidTouched ? Number(paid) || 0 : total;
  const qarz = Math.max(0, total - paidNum);
  // Yangi mijoz "ma'lum" hisoblanishi uchun ism VA telefon kerak — faqat
  // ismli (telefoniz) mijoz qarz blokini chetlab o'tmasligi uchun.
  const hasNewClient =
    addingNew && newName.trim().length > 0 && !isBlankPhoneInput(newPhone);
  const effAnon = !client && !hasNewClient;
  const needClient = qarz > 0;

  const usedIds = useMemo(() => new Set(lines.map((l) => l.product.id)), [lines]);

  function setLine(i: number, patch: Partial<Line>) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  function addProduct(p: Product) {
    setLines((ls) =>
      ls.some((l) => l.product.id === p.id)
        ? ls
        : [
            ...ls,
            {
              product: p,
              miqdor: String(Math.min(1, Number(p.joriy_ostatka))),
              // Kasrni olib tashlaymiz ("15000.00" → "15000"), aks holda
              // MoneyInput nuqtani yo'qotib ×100 ko'rsatardi.
              sotuv_narx: String(Math.round(Number(p.sotuv_narx))),
            },
          ],
    );
  }

  function cancelNewClient() {
    setAddingNew(false);
    setNewName("");
    setNewPhone("+998");
    setNewPlate("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (lines.length === 0) {
      setErr(t("sotuvlar.form.atLeastOne"));
      return;
    }
    // Ombor qoidasi (FE) — hech bir qator ostatkadan oshmasligi kerak.
    const over = lines.find((l) => (Number(l.miqdor) || 0) > Number(l.product.joriy_ostatka));
    if (over) {
      setErr(t("sotuvlar.form.overStock", { nomi: over.product.nomi }));
      return;
    }
    if (needClient && effAnon) {
      setErr(t("sotuvlar.form.debtNeedsClient"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const mode = client
        ? "existing"
        : addingNew && newName.trim()
          ? "new"
          : "anonymous";
      const body: Record<string, unknown> = {
        customer_mode: mode,
        tolangan_summa: String(paidNum),
        // Nol to'lovda usul yuborilmaydi — pul harakat qilmagan.
        ...(usul && paidNum > 0 ? { tolov_usuli: usul } : {}),
        lines: lines.map((l) => ({
          product_id: l.product.id,
          miqdor: l.miqdor || "0",
          sotuv_narx: l.sotuv_narx || "0",
        })),
      };
      if (client) body.client_id = client.id;
      if (mode === "new")
        body.new_client = {
          full_name: newName.trim(),
          phone: newPhone.trim(),
          plate_number: newPlate.trim() || null,
        };
      const sale = await api.createSale(body);
      (sale.ogohlantirishlar || []).forEach((w) => toast.warning(w));
      toast.success(t("sotuvlar.form.saved"));
      onSaved();
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex max-h-[85vh] w-full flex-col">
      {/* 1. Qotirilgan header: sarlavha + qidiruv + xaridor */}
      <div className="flex-none space-y-3 border-b p-4 sm:p-5">
        <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">{t("sotuvlar.form.title")}</h2>
        <ProductCombobox
          products={products}
          excludeIds={usedIds}
          onPick={addProduct}
          unavailable={(p) => Number(p.joriy_ostatka) <= 0}
          disabled={busy}
        />
        {/* Mijoz: tanlangan / yangi qo'shish / qidiruv (bo'sh = anonim) */}
        <div className="space-y-1.5">
          {client ? (
            <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
              <span className="font-medium">{client.full_name} <span className="text-muted-foreground">· {formatUzPhone(client.phone)}</span></span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setClient(null)}>{t("sotuvlar.form.changeClient")}</Button>
            </div>
          ) : addingNew ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} disabled={busy} placeholder={t("sotuvlar.form.newNamePlaceholder")} />
                <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} disabled={busy} placeholder={t("sotuvlar.form.newPhonePlaceholder")} inputMode="tel" />
                <Button type="button" variant="ghost" size="icon" onClick={cancelNewClient} aria-label={t("shared.delete")}>
                  <X className="size-4" />
                </Button>
              </div>
              <Input value={newPlate} onChange={(e) => setNewPlate(e.target.value)} disabled={busy} placeholder={t("sotuvlar.form.newPlatePlaceholder")} />
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder={t("sotuvlar.form.clientSearchPlaceholder")} disabled={busy} />
                {clientResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 max-h-40 divide-y overflow-y-auto rounded-md border bg-card shadow-lg">
                    {clientResults.map((c) => (
                      <button key={c.id} type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => { setClient(c); setClientResults([]); setClientQuery(""); }}>
                        {c.full_name} <span className="text-muted-foreground">· {formatUzPhone(c.phone)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button type="button" variant="outline" onClick={() => setAddingNew(true)} disabled={busy} className="shrink-0">
                <Plus />
                {t("sotuvlar.form.addNewClient")}
              </Button>
            </div>
          )}
          {needClient && effAnon && (
            <p className="text-xs text-destructive">{t("sotuvlar.form.debtNeedsClient")}</p>
          )}
        </div>
      </div>

      {/* 2. Yagona scroll zonasi: tovarlar jadvali */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-[120px]" />
            <col className="w-[132px]" />
            <col className="w-[140px]" />
            <col className="w-[40px]" />
          </colgroup>
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
            <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2 text-left">{t("sotuvlar.form.tovar")}</th>
              <th className="px-2 py-2 text-center">{t("sotuvlar.form.miqdor")}</th>
              <th className="px-2 py-2 text-right">{t("sotuvlar.form.narx")}</th>
              <th className="px-3 py-2 text-right">{t("sotuvlar.form.jami")}</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-muted-foreground">
                  <PackageSearch className="mx-auto mb-2 size-6 opacity-60" />
                  {t("sotuvlar.form.emptyHint")}
                </td>
              </tr>
            ) : (
              lines.map((l, i) => {
                const avail = Number(l.product.joriy_ostatka);
                const over = (Number(l.miqdor) || 0) > avail;
                return (
                  <tr key={l.product.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <div className="truncate font-medium" title={l.product.nomi}>{l.product.nomi}</div>
                      <div className={cn("truncate text-xs", over ? "font-medium text-destructive" : "text-muted-foreground")}>
                        {avail} {l.product.birlik}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <QtyStepper value={l.miqdor} onChange={(v) => setLine(i, { miqdor: v })} disabled={busy} max={avail} />
                    </td>
                    <td className="px-2 py-2">
                      <MoneyInput value={l.sotuv_narx} onChange={(v) => setLine(i, { sotuv_narx: v })} disabled={busy} className="h-8 px-2 text-right tabular-nums" />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {fmtMoney((Number(l.miqdor) || 0) * (Number(l.sotuv_narx) || 0))}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button type="button" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} disabled={busy} className="text-muted-foreground hover:text-destructive disabled:opacity-50" aria-label={t("shared.delete")}>
                        <X className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        {err && (
          <div className="m-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div>
        )}
      </div>

      {/* 3. Qotirilgan footer: to'langan summa + Jami/Qarz + tugmalar */}
      <div className="flex-none rounded-b-lg border-t bg-muted/30 p-4 sm:p-5">
        <div className="flex items-end justify-between gap-3">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wider text-muted-foreground">{t("sotuvlar.form.tolangan")}</label>
            <MoneyInput
              value={paid}
              onChange={(v) => { setPaidTouched(true); setPaid(v); }}
              disabled={busy}
              className="h-9 w-40 text-right tabular-nums"
              placeholder="0"
            />
            {/* Usul — nol to'lovda o'chiriladi (server ham rad etadi). */}
            <PaymentMethodSelect
              id="s-usul"
              value={usul}
              onChange={setUsul}
              disabled={busy || paidNum <= 0}
              className="w-40 space-y-1"
            />
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{t("sotuvlar.form.jami")}</div>
            <div className="text-2xl font-bold tabular-nums">{fmtMoney(total)}</div>
            {qarz > 0 && (
              <div className="text-sm font-medium tabular-nums text-destructive">
                {t("sotuvlar.form.qarz")}: {fmtMoney(qarz)}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-none">{t("sotuvlar.cancel")}</Button>
          <Button type="submit" disabled={busy || lines.length === 0} className="flex-1">
            {busy ? t("sotuvlar.form.submitting") : t("sotuvlar.form.submit")}
          </Button>
        </div>
      </div>
    </form>
  );
}
